import axios from 'axios';
import TagManager from 'react-gtm-module';

import Config from '@shared/config/constants/config-main';
import {
  prepareSMSURIForInAppRedirect,
  getParameterByName,
  appendTextScript,
  prepareSMSURI,
} from '@shared/utils/general';

import {
  DC_LOCAL_API_SOURCE_NAME,
  ORDER_ID_DC_SS_NAME,
  PRICE_DC_SS_NAME,
  shouldRunDCEvent,
} from '@shared/utils/dc-events';

import { MA_EVINA_SCRIPT_SECOND_CHALLENGE } from '@features/phone-subscription/model/hooks/useMoroccoDVHEFlowOTP';
import { IQ_ZAIN_ANTIFRAUD_ID_SS_NAME, clearIQZainAntifraudSession } from '@features/phone-subscription/model/hooks/useIQZainAntifraud';
import { LAYOUT_TYPES, STC_APPROVED_SECURE_SOURCE } from '@shared/config/constants/app';
import { decryptSTCResponse } from '@shared/utils/decrypt-stc-response';
import { FacebookService } from '@shared/services/FacebookService';
import { startFlowLogger } from '@shared/utils/start-flow-logger';
import { decryptAffiliate } from '@shared/api/decryptAffiliate';
import { PAYMENT_GATEWAYS, PAYMENT_METHODS } from '@entities/payment-plan';
import { COUNTRY_SHORT_NAMES } from '@entities/country/model/constants';
import { isMoroccoDvContext as matchesMoroccoDv } from '@entities/operator';
import { jotaiStore } from '@shared/store/jotai/jotai-store';
import { languageAtom } from '@entities/language/store';
import { decodeErrorText } from '@shared/utils/decode-error-text';
import { LogService } from '@shared/services/LogService';
import { clearAlerts, setAlert } from '@shared/store/slices/alert.actions';
import { IConfiguration } from '@entities/dcb/model/types';
import { Helpers } from '@shared/utils/Helpers';

import * as Dcb from '@entities/dcb/store/dcb.actions';
import { dcbAtom, initialState as dcbInitialSnapshot } from '@entities/dcb/store/dcb.atom';
import { isAg26MarketingVariation } from '@shared/ui/unlock-vuz-banner';

const API_URL = Config.API_URL;
const WEB_PAGE_URL = Config.WEB_PAGE_URL;

export const changeOperator = (operatorItem) => {
  Dcb.applyChangeOperatorSelection({
    operatorName: operatorItem.name,
    operatorValue: operatorItem.value,
    operatorLogo: operatorItem.logo,
    isShownMsisdn: operatorItem.isShownMsisdn,
    fullOperatorDetails: operatorItem,
  });

  if (operatorItem.headerEnrichmentUrl && !operatorItem.disableRedirection) {
    window.location.href = operatorItem.headerEnrichmentUrl;
    console.log(`Redirect to ${operatorItem.headerEnrichmentUrl}`);
  }
};

const buildHomeDcbUrl = (inputFields, urlParams, query) => {
  let homeUrl = `${API_URL}/home`;
  const country = getCountry(inputFields);
  let operatorPreSelected = 'NONE';

  if (inputFields.channel && inputFields.method) {
    if (inputFields.lang)
      homeUrl = `${homeUrl}/${inputFields.lang}/${inputFields.country}/${inputFields.channel}/${inputFields.method}/${inputFields.fbPixelId}/${inputFields.clickId}`;
  } else if (inputFields.mcc && inputFields.mnc && inputFields.subscriberId) {
    homeUrl = `${homeUrl}/inapp`;
    if (inputFields.lang) homeUrl = `${homeUrl}/${inputFields.lang}`;
    if (country) homeUrl = `${homeUrl}/${country}`;
    if (inputFields.mcc) homeUrl = `${homeUrl}/${inputFields.mcc}`;
    if (inputFields.mnc) homeUrl = `${homeUrl}/${inputFields.mnc}`;
    if (inputFields.subscriberId) homeUrl = `${homeUrl}/${inputFields.subscriberId}`;
  } else {
    if (inputFields.isQuery) homeUrl = `${homeUrl}/query`;
    const lang = inputFields.lang || 'en';
    homeUrl = `${homeUrl}/${lang}`;
    if (country) homeUrl = `${homeUrl}/${country}`;
    if (inputFields.billingPlan) homeUrl = `${homeUrl}/billingPlan/${inputFields.billingPlan}`;
    if (inputFields.operator) homeUrl = `${homeUrl}/${inputFields.operator}`;
    if (inputFields.msisdn) homeUrl = `${homeUrl}/${inputFields.msisdn}`;
    if (inputFields.operator) operatorPreSelected = inputFields.operator;
    if (inputFields.billingOption) homeUrl = `${homeUrl}/package/${inputFields.billingOption}`;
  }

  if (inputFields.source) {
    homeUrl = `${homeUrl}?source=${inputFields.source}`;
    if (inputFields.utm_source) homeUrl = `${homeUrl}&utm_source=${inputFields.utm_source}`;
    if (inputFields.utm_medium) homeUrl = `${homeUrl}&utm_medium=${inputFields.utm_medium}`;
    if (inputFields.utm_campaign) homeUrl = `${homeUrl}&utm_campaign=${inputFields.utm_campaign}`;
    if (inputFields.clickId) homeUrl = `${homeUrl}&clickId=${inputFields.clickId}`;
    if (inputFields.fbPixelId) homeUrl = `${homeUrl}&fbPixelId=${inputFields.fbPixelId}`;
    if (inputFields.channel) homeUrl = `${homeUrl}&channel=${inputFields.channel}`;
  } else if (
    inputFields.utm_source ||
    inputFields.utm_medium ||
    inputFields.utm_campaign ||
    inputFields.clickId ||
    inputFields.fbPixelId ||
    inputFields.channel
  ) {
    homeUrl = `${homeUrl}?`;
    if (inputFields.utm_source) homeUrl = `${homeUrl}utm_source=${inputFields.utm_source}&`;
    if (inputFields.utm_medium) homeUrl = `${homeUrl}utm_medium=${inputFields.utm_medium}&`;
    if (inputFields.utm_campaign) homeUrl = `${homeUrl}utm_campaign=${inputFields.utm_campaign}&`;
    if (inputFields.clickId) homeUrl = `${homeUrl}clickId=${inputFields.clickId}&`;
    if (inputFields.fbPixelId) homeUrl = `${homeUrl}fbPixelId=${inputFields.fbPixelId}&`;
    if (inputFields.channel) homeUrl = `${homeUrl}channel=${inputFields.channel}&`;
    homeUrl = homeUrl.substr(0, homeUrl.length - 1);
  }

  if (inputFields.allQueryParams && inputFields.allQueryParams.length > 0) {
    if (!homeUrl.includes('?')) homeUrl = `${homeUrl}?`;
    for (const qParam of inputFields.allQueryParams) {
      homeUrl = `${homeUrl}&${qParam.qKey}=${qParam.qValue}`;
    }
  }

  if (query) {
    const isDVRedirection = query.get('action') === 'dvredirection';
    const isHERedirection = query.get('action') === 'heredirection';
    const rawMobilePlan = query.get('mobileplan') || '';
    const queryUserType =
      isDVRedirection || matchesMoroccoDv(inputFields.source)
        ? rawMobilePlan.toLowerCase()
        : rawMobilePlan;
    const isMobilePlanPresented = queryUserType === 'prepaid' || queryUserType === 'postpaid';

    if (isMobilePlanPresented) {
      homeUrl = homeUrl.includes('?')
        ? `${homeUrl}&mobileplan=${queryUserType}`
        : `${homeUrl}?mobileplan=${queryUserType}`;
    }

    if (isDVRedirection) {
      const billingOption = query.get('billingOption') || 'weekly';
      homeUrl = homeUrl.includes('?')
        ? `${homeUrl}&isQueryCallback=1&billingOption=${billingOption}`
        : `${homeUrl}?isQueryCallback=1&billingOption=${billingOption}`;
    }

    if (isHERedirection) {
      homeUrl = homeUrl.includes('?')
        ? `${homeUrl}&isQueryCallback=1`
        : `${homeUrl}?isQueryCallback=1`;
    }
  }

  if (urlParams?.country?.toUpperCase() === COUNTRY_SHORT_NAMES.KW) {
    const urlReferrer = encodeURIComponent(window.location.href);
    const adUrlReferrer = encodeURIComponent(document.referrer || '');
    homeUrl = homeUrl.includes('?')
      ? `${homeUrl}&urlReferrer=${urlReferrer}&adUrlReferrer=${adUrlReferrer}`
      : `${homeUrl}?urlReferrer=${urlReferrer}&adUrlReferrer=${adUrlReferrer}`;
  }

  return { homeUrl, operatorPreSelected };
};

const ERROR_BY_LANG = {
  en: 'Error processing request. Your country might not be supported',
  ar: 'طلب معالجة خطأ. قد لا يتم دعم بلدك',
  fr: "Erreur lors du traitement de la demande. Votre pays n'est peut-être pas pris en charge",
  es: 'Error al procesar la solicitud. Es posible que su país no sea compatible',
  id: 'Permintaan tidak bisa diproses. Aplikasi ini tidak tersedia di negara Anda',
  ms: 'Ralat semasa memproses permintaan. Negara anda mungkin tidak disokong',
  tr: 'İstek işlenirken hata oluştu. Ülkeniz desteklenmeyebilir',
  no: 'Feil under behandling av forespørsel. Landet ditt støttes kanskje ikke',
};

const PIN_ERROR_BY_LANG = {
  en: 'Something is wrong with your PIN code, please make sure it is written correct and try again',
  ar: 'هناك خطأ ما في الكود الخاص بك ، يرجى التأكد من كتابته بشكل صحيح والمحاولة مرة أخرى',
  fr: "Il y a un problème avec votre code PIN, veuillez vous assurer qu'il est écrit correctement et réessayer",
  es: 'Algo está mal con su código PIN, asegúrese de que esté escrito correctamente y vuelva a intentarlo',
  id: 'Ada yang salah dengan kode PIN Anda, harap pastikan bahwa penulisannya benar dan coba lagi',
  ms: 'Ada yang tidak kena dengan kod PIN anda, pastikan ia ditulis dengan betul dan cuba lagi',
  tr: 'PIN kodunuzda bir sorun var, lütfen doğru yazıldığından emin olun ve tekrar deneyin',
  no: 'Noe er galt med PIN -koden din. Kontroller at den er riktig skrevet og prøv igjen',
};

const getLocalizedError = (map, lang) => map[lang] || map.en;

const INSUFFICIENT_CREDIT_BY_LANG = {
  en: 'You do not have enough credit to complete this subscription. Please recharge and try again.',
  ar: 'رصيدك غير كافٍ لإتمام الاشتراك. يرجى إعادة الشحن والمحاولة مرة أخرى.',
  fr: "Votre crédit est insuffisant pour finaliser l'abonnement. Veuillez recharger et réessayer.",
};

const SUPPORT_ERROR_BY_LANG = {
  en: 'Error processing request. Please contact support',
  ar: 'حدث خطأ أثناء معالجة الطلب. يرجى التواصل مع الدعم',
  fr: 'Erreur lors du traitement de la demande. Veuillez contacter le support',
};

// DV 4110 mapped by the API: send PIN → 10143, confirm PIN → 10373.
const INSUFFICIENT_CREDIT_CODES = new Set(['10143', '10373']);

const isInsufficientCreditCode = (code: unknown) =>
  INSUFFICIENT_CREDIT_CODES.has(String(code ?? ''));

const isMoroccoDvContext = (formData?: { source?: string; operator?: string }) => {
  const dcb = jotaiStore.get(dcbAtom);

  const firstOperator = dcb?.homeData?.operators?.[0] as { value?: string } | undefined;

  return matchesMoroccoDv(
    formData?.operator,
    formData?.source,
    dcb?.operator?.value,
    dcb?.homeData?.source,
    dcb?.source,
    dcb?.subscribeData?.operatorName,
    firstOperator?.value
  );
};

const currentLang = (formData?: { lang?: string; language?: string }) =>
  formData?.lang || formData?.language || jotaiStore.get(languageAtom)?.locale || 'en';

const localizedMessageFromObject = (message: unknown, lang: string) => {
  if (!message || typeof message !== 'object') return '';

  const record = message as Record<string, unknown>;
  const localized = record[lang] ?? record.en ?? record.fr ?? record.ar;

  return typeof localized === 'string' ? decodeErrorText(localized) : '';
};

const resolveSubscribeErrorMessage = (message: unknown, code: unknown, formData?: { lang?: string; language?: string }) => {
  const lang = currentLang(formData);

  if (isInsufficientCreditCode(code)) {
    return getLocalizedError(INSUFFICIENT_CREDIT_BY_LANG, lang);
  }

  const fromObject = localizedMessageFromObject(message, lang);
  if (fromObject) return fromObject;

  return decodeErrorText(message);
};

const moroccoDvAlertOptions = { dismissible: true };

const showSubscribeError = async (message: unknown, code: unknown, formData?: { lang?: string; language?: string; source?: string; operator?: string }) => {
  if (!isMoroccoDvContext(formData)) {
    await setAlert(message, 'danger', isInsufficientCreditCode(code) ? null : undefined);
    return;
  }

  await setAlert(resolveSubscribeErrorMessage(message, code, formData), 'danger', null, moroccoDvAlertOptions);
};

const showSupportError = async (formData?: { lang?: string; language?: string; source?: string; operator?: string }) => {
  if (!isMoroccoDvContext(formData)) {
    await setAlert(SUPPORT_ERROR_BY_LANG.en, 'danger');
    return;
  }

  await setAlert(
    getLocalizedError(SUPPORT_ERROR_BY_LANG, currentLang(formData)),
    'danger',
    null,
    moroccoDvAlertOptions
  );
};

const buildFlowSearch = (lang?: string) => {
  const params = new URLSearchParams();
  const noRedirect = getParameterByName('noRedirect');
  const isTVLayout = getParameterByName('layout_type') === LAYOUT_TYPES.TV;

  if (noRedirect) params.set('noRedirect', 'true');
  if (isTVLayout) params.set('layout_type', 'tv');
  if (lang) params.set('lang', lang);

  return params.toString();
};

const appendLangToPath = (path: string, lang?: string) => {
  if (!lang) return path;

  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  params.set('lang', lang);
  return `${pathname}?${params.toString()}`;
};

export const getHomeDcb = async (
  inputFields,
  urlParams,
  apiSource,
  manualPaymentMethod,
  paymentGateway = PAYMENT_GATEWAYS.STRIPE,
  query: URLSearchParams | null = null
) => {
  Dcb.writeHomeRequested();
  const config = Helpers.getAxiosHeadersConfig();

  // @ts-ignore
  window.PapInitializeTracking();

  const { homeUrl, operatorPreSelected } = buildHomeDcbUrl(inputFields, urlParams, query);

  try {
    const isSTCApprovedSource = apiSource === STC_APPROVED_SECURE_SOURCE;

    let data;
    const res = await axios.get(homeUrl, config);

    if (isSTCApprovedSource) {
      const stcDescyptedResponse = await decryptSTCResponse(res.data);
      const getFirstNormalSymbolIndex = stcDescyptedResponse?.indexOf('"') || 0;

      const newFormattedData = `{${stcDescyptedResponse?.slice(
        getFirstNormalSymbolIndex,
        stcDescyptedResponse.length
      )}`
        ?.replaceAll('', '')
        ?.replaceAll('', '');

      const newParsedData = JSON.parse(newFormattedData) || {};
      data = newParsedData?.data || {};
    } else {
      data = res?.data.data || {};
    }

    if (res?.data?.statusCode === 403 || !res?.data?.data) {
      Dcb.writeFlowErrorStoppingLoading(null);

      if (res?.data?.statusCode === 403 && res.data?.message) {
        alert(res.data?.message);
      }

      return;
    }

    if (data?.script) {
      appendTextScript(data.script);
    }

    if (data.redirectURL && !inputFields.disableRedirection) {
      window.location.href = data.redirectURL;
      console.log(`Redirect to ${data.redirectURL}`);
      return;
    }

    const receivedOperators = data.operators;

    if (operatorPreSelected !== 'NONE') {
      const chosenOperator = receivedOperators.filter((op) => op.value === operatorPreSelected);
      const otherOperators = receivedOperators.filter((op) => op.value !== operatorPreSelected);
      data.operators = [...chosenOperator, ...otherOperators];
    } else if (inputFields.mcc && inputFields.mnc) {
      const resOp = await axios.get(
        `${API_URL}/home/operator/${inputFields.mcc}/${inputFields.mnc}`
      );
      const matchedOperator = resOp.data.data;
      const chosenOperator = receivedOperators.filter((op) => op.value === matchedOperator.value);
      const otherOperators = receivedOperators.filter((op) => op.value !== matchedOperator.value);
      data.operators = [...chosenOperator, ...otherOperators];
    }

    if (data.termsText && data.termsText !== '') {
      data.fullTCText = data.termsText;
      data.dataRetrievedLanguage = inputFields.lang || 'en';

      const pageLink = window.location.href;
      if (pageLink.includes('promo') && !pageLink.includes('new') && data.operators) {
        data.operators = data.operators.filter((operatorItem) => operatorItem.value !== 'KSA-STC');
      }
    }

    const { utm_source } = inputFields;
    if (utm_source) {
      const decrypted_utm_source = await decryptAffiliate(utm_source);
      if (decrypted_utm_source) inputFields.utm_source = decrypted_utm_source;
    }

    startFlowLogger(inputFields, apiSource, manualPaymentMethod, data.country, paymentGateway);
    Dcb.writeStoredHomeUrl(homeUrl);
    Dcb.writeHomeReceived(data);
  } catch (err) {
    console.log('getHomeDcbResponseError', err);
    Dcb.writeFlowErrorStoppingLoading(null);
    await setAlert(getLocalizedError(ERROR_BY_LANG, inputFields.lang), 'danger');
  }
};
const getCountry = (inputFields) => {
  if (inputFields.selectedCountry || inputFields.country) {
    return inputFields.selectedCountry ? inputFields.selectedCountry : inputFields.country;
  }
  return undefined;
};

const buildSubscribeRequestBody = (formData) => {
  let phone;
  let countryCode;

  if (formData.mobile) phone = formData.mobile;
  else if (formData.phone) phone = formData.phone;

  if (formData.mobileCountryCode) countryCode = formData.mobileCountryCode;
  else if (formData.countryCode) countryCode = formData.countryCode;

  const body: Record<string, any> = {
    subscriptionMethodType: 'mobile',
    mobile: formData.isShownMsisdn ? phone : 'no number',
    mobileCountryCode: countryCode?.substring(1),
    userOperator: formData.operator,
    subscriberId: formData.subscriberId ? formData.subscriberId : '',
    lang: formData.lang || formData.language || 'en',
    source: formData.source ? formData.source : 'api-web',
    channel: formData.channel ? formData.channel : 'organic',
    method: formData.method ? formData.method : 'web',
    fbPixelId: formData.fbPixelId ? formData.fbPixelId : '981697515182465',
    clickId: formData.clickId ? formData.clickId : '',
    utm_source: formData.utm_source ? formData.utm_source : '',
    utm_medium: formData.utm_medium ? formData.utm_medium : '',
    utm_campaign: formData.utm_campaign ? formData.utm_campaign : '',
    billingOption: formData.billingOption ? formData.billingOption : 'weekly',
    SubscriptionOptionMobile: '',
    redirectOTP: formData.isRedirectOTP ? formData.isRedirectOTP : false,
    redirectUrlBase: WEB_PAGE_URL,
    urlReferrer: window.location.href,
    adUrlReferrer: document.referrer || '',
    offerCode: formData.offerCode ? formData.offerCode : '',
    shieldUniqid: formData.shieldUniqid ? formData.shieldUniqid : '',
    userId: formData.userId,
    correlationId: formData?.correlationId ? formData.correlationId : '',
    antiFraudUniqueId: formData?.antiFraudUniqueId ? formData.antiFraudUniqueId : '',
    operationId: formData?.operationId ? formData.operationId : '',
    identificationId: formData?.identificationId ? formData.identificationId : '',
    ga4_session_id: formData?.DLSessionId ? formData?.DLSessionId : '',
    isHeaderEnrichment: formData?.isHeaderEnrichment ? formData.isHeaderEnrichment : '',
  };

  if (formData.aggregatorClickId) {
    body.aggregatorClickId = formData.aggregatorClickId;
  }

  return body;
};

const decryptSTCResponseToData = async (rawResponse) => {
  const stcDescyptedResponse = await decryptSTCResponse(rawResponse);
  const getFirstNormalSymbolIndex = stcDescyptedResponse?.indexOf('"') || 0;

  const newFormattedData = `{${stcDescyptedResponse?.slice(
    getFirstNormalSymbolIndex,
    stcDescyptedResponse.length
  )}`;

  const diffSymbolIntheEnd = newFormattedData?.[newFormattedData.length - 1];

  const finalFormattedData =
    diffSymbolIntheEnd !== '}'
      ? newFormattedData.replaceAll(diffSymbolIntheEnd, '')
      : newFormattedData;

  const newParsedData = JSON.parse(finalFormattedData) || {};
  const data = newParsedData?.data || {};
  const statusCode = data?.message?.trim().toLowerCase().includes('success')
    ? 200
    : newParsedData?.statusCode || '';

  return {
    data,
    statusCode,
    message: newParsedData?.message || '',
    sessionId: newParsedData?.sessionId || '',
  };
};

const handleSMSRedirect = (data, isInappRedirect) => {
  const phoneNumber = data.smsShortCode;
  const message = encodeURIComponent(data.smsKeyword);

  if (isInappRedirect) {
    const currentLink = window.location;
    const redirectUrl = `${currentLink}?${prepareSMSURIForInAppRedirect(phoneNumber, message)}`;
    window.location = redirectUrl as string & Location;
    return;
  }

  const smsUri = prepareSMSURI(phoneNumber, message);
  Dcb.writeSubscribeCompleted({});
  window.location = smsUri as string & Location;
  localStorage.removeItem('detectedOperators');
};

const handleSubscribeOtpValidation = async (data, formData, history) => {
  Dcb.writeOtpSubmitStarted();
  const config = Helpers.getAxiosHeadersConfig();
  const MAHESecondClickId = sessionStorage.getItem(MA_EVINA_SCRIPT_SECOND_CHALLENGE) || '';
  const sessionIdSpecialFlow = data?.sessionId || '0';
  const iraqZainAntiFraudUniqueId = sessionStorage.getItem(IQ_ZAIN_ANTIFRAUD_ID_SS_NAME) || '';
  const resolvedAntiFraudUniqueId =
    formData?.antiFraudUniqueId || formData?.antifraudUniqueId || iraqZainAntiFraudUniqueId || '';

  const otpRequestBody = {
    sessionId: data?.sessionId || '',
    lang: formData?.lang || formData?.language || 'en',
    otp: '0000',
    antifraudUniqueId: resolvedAntiFraudUniqueId,
    antiFraudUniqueId: resolvedAntiFraudUniqueId,
    clickId: MAHESecondClickId,
  };

  LogService.dcbEnterOtpEvent(formData.otp);
  LogService.dcbClickButtonVerify(formData.otp);

  try {
    const res = await axios.post(`${API_URL}/home/subscribe/otp`, otpRequestBody, config);
    const { data: otpData, statusCode, message } = res.data || {};

    if (otpData) {
      otpData.otpError = !(statusCode === 200 || statusCode === 10300);
    }

    Dcb.writeOtpResult(otpData);

    if ((statusCode === 200 || statusCode === 10300) && otpData?.activationURL) {
      clearAlerts();
      clearIQZainAntifraudSession();
      LogService.dcbFinishPinFlow();

      TagManager.dataLayer({
        dataLayer: {
          event: 'subscribe',
          eventCategory: 'DCB',
          eventAction: 'subscribe',
          eventValue: otpData.price || 'USD',
          eventLabel: 'Plus Subscription',
        },
        dataLayerName: 'SubscriptionDataLayer',
      });

      FacebookService.trackSubscribe(otpData.price);

      if (otpData.papName) {
        const decryptedPapName = await decryptAffiliate(otpData.papName);
        if (decryptedPapName) otpData.papName = decryptedPapName;
      }

      // @ts-ignore
      window.PapRegisterLead(
        formData.sessionId,
        otpData.papData1,
        otpData.papData2,
        otpData.papData3,
        otpData.papName
      );

      Dcb.setPromoStep(1);

      try {
        if (localStorage) {
          localStorage.setItem('isPromoValid', 'false');
          localStorage.setItem('appliedPromocode', '');
        }
      } catch (err) {}

      history.push({
        pathname: `/thankyou/${sessionIdSpecialFlow}`,
        state: { activationURL: otpData.activationURL, isWhite: false },
      });
    } else {
      await showSubscribeError(message, otpData?.apiUnifiedResponseCode, formData);
    }
  } catch (err) {
    console.log('submitOTPResponseError', err);
    const moroccoDv = isMoroccoDvContext(formData);
    await setAlert(
      getLocalizedError(PIN_ERROR_BY_LANG, otpRequestBody.lang),
      'danger',
      moroccoDv ? null : undefined,
      moroccoDv ? moroccoDvAlertOptions : undefined
    );
    Dcb.writeOtpFailure(null);
  }
};

export const subscribeUsingPhone = async (
  formData,
  history,
  configuration: IConfiguration = {},
  isInappRedirect = false,
  setStep?: any
) => {
  const {
    redirectRoute = null,
    isHlrlookup = false,
    prevErrors,
    phoneNumber,
    callback = () => {},
    onCatchCallback = () => {},
  } = configuration;

  Dcb.writeSubscribeRequested();
  const config = Helpers.getAxiosHeadersConfig();

  const subscribeRequestBody = buildSubscribeRequestBody(formData);

  LogService.dcbClickButtonSubscribe({
    operator: formData.operator,
    msisdn: formData.phone,
  });

  try {
    const isSTCApprovedSource = formData?.source === STC_APPROVED_SECURE_SOURCE;

    Dcb.writeSubscribeRequestData(subscribeRequestBody);

    const res = await axios.post(`${API_URL}/home/subscribe`, subscribeRequestBody, config);

    let data;
    let statusCode;
    let message;
    let sessionId;

    if (isSTCApprovedSource) {
      ({ data, statusCode, message, sessionId } = await decryptSTCResponseToData(res.data));
    } else {
      data = res.data?.data;
      statusCode = res.data?.statusCode;
      message = res.data?.message;
      sessionId = res.data?.sessionId;
    }

    if (data?.isSMSRedirect) {
      handleSMSRedirect(data, isInappRedirect);
      return;
    }

    if (formData.selectedPrice && data) {
      data.selectedPrice = formData.selectedPrice;
    }

    const apiSourceFromSessionStorage = sessionStorage.getItem(DC_LOCAL_API_SOURCE_NAME) || '';
    const isInitiativeFlow = shouldRunDCEvent(apiSourceFromSessionStorage);

    if (isInitiativeFlow) {
      sessionStorage.setItem(PRICE_DC_SS_NAME, data?.price || 0);
      sessionStorage.setItem(ORDER_ID_DC_SS_NAME, data?.sessionId || 0);
    }

    Dcb.writeSubscribeCompleted(data);

    if (statusCode === 401 && isHlrlookup) {
      onCatchCallback(
        { translateKey: 'something_went_wrong' },
        message
          ? { value: message, code: data?.apiUnifiedResponseCode || 0 }
          : { translateKey: 'something_went_wrong_proceed_with_alternative_payment' }
      );
      Dcb.setDefaultPaymentMethod(PAYMENT_METHODS.Card);
      return;
    }

    if (statusCode !== 200) {
      const unifiedCode = data?.apiUnifiedResponseCode;
      const moroccoDv = isMoroccoDvContext(formData);
      const storedMessage = moroccoDv
        ? resolveSubscribeErrorMessage(message, unifiedCode, formData)
        : message;

      if (phoneNumber) {
        const accumulated = prevErrors
          ? { ...prevErrors, [phoneNumber]: storedMessage }
          : { [phoneNumber]: storedMessage };
        sessionStorage.setItem('detectOperatorError', JSON.stringify(accumulated));
      }

      // Insufficient credit is an operator message. Leave it on the form
      // instead of opening the card-payment fallback, which would hide it.
      if (!isInsufficientCreditCode(unifiedCode)) {
        onCatchCallback(
          { translateKey: 'something_went_wrong' },
          message
            ? {
                value: storedMessage,
                code: unifiedCode || 0,
              }
            : { translateKey: 'something_went_wrong_proceed_with_alternative_payment' }
        );
      }
      await showSubscribeError(message, unifiedCode, formData);
      return;
    }

    clearAlerts();

    const { msisdn, isRedirect, redirectURL, subscriberId, operator, isValidation } = data || {};

    // Note: only for Morocco orange flow
    if (isValidation) {
      await handleSubscribeOtpValidation(data, formData, history);
      return;
    }

    if (isRedirect && isRedirect === true && redirectURL) {
      LogService.dcbHostedFlow(
        { plusDCBSessionId: sessionId },
        { redirectURL, operator, msisdn }
      );
      window.location.href = redirectURL;
    } else if (redirectRoute) {
      LogService.dcbOtpFlow({
        subscriberId,
        plusDCBSessionId: sessionId,
        operator: formData.operator,
        msisdn,
      });
      history.push(appendLangToPath(redirectRoute, subscribeRequestBody.lang));
    } else if (setStep) {
      setStep();
    } else {
      LogService.dcbOtpFlow({
        subscriberId,
        plusDCBSessionId: sessionId,
        operator: formData.operator,
        msisdn,
      });

      const confirmPinSearch = buildFlowSearch(subscribeRequestBody.lang);
      history.push(`/confirm-pin${confirmPinSearch ? `?${confirmPinSearch}` : ''}`);
    }
  } catch (err) {
    onCatchCallback(
      { translateKey: 'something_went_wrong' },
      { translateKey: 'something_went_wrong_proceed_with_alternative_payment' }
    );
    await showSupportError(formData);
    Dcb.writeFlowErrorStoppingLoading(null);
  } finally {
    callback();
  }
};

export const submitOTP = async (formData, history, redirectRoute = null) => {
  Dcb.writeOtpSubmitStarted();
  const config = Helpers.getAxiosHeadersConfig();

  const resolvedAntiFraudUniqueId =
    formData?.antiFraudUniqueId ||
    formData?.antifraudUniqueId ||
    sessionStorage.getItem(IQ_ZAIN_ANTIFRAUD_ID_SS_NAME) ||
    '';

  const otpRequestBody = {
    sessionId: formData.sessionId,
    lang: formData.lang,
    otp: formData.otp,
    antifraudUniqueId: resolvedAntiFraudUniqueId,
    antiFraudUniqueId: resolvedAntiFraudUniqueId,
    clickId: formData?.clickId || '',
    ga4_session_id: formData.ga4_session_id || '',
  };

  LogService.dcbEnterOtpEvent(formData.otp);
  LogService.dcbClickButtonVerify(formData.otp);

  try {
    const res = await axios.post(`${API_URL}/home/subscribe/otp`, otpRequestBody, config);
    const { data, statusCode, message } = res.data;

    if (data) {
      data.otpError = statusCode !== 200;
    }

    Dcb.writeOtpResult(data);

    if (statusCode === 200 && data.activationURL) {
      clearAlerts();
      clearIQZainAntifraudSession();
      LogService.dcbFinishPinFlow();

      TagManager.dataLayer({
        dataLayer: {
          event: 'subscribe',
          eventCategory: 'DCB',
          eventAction: 'subscribe',
          eventValue: data.price || 'USD',
          eventLabel: 'Plus Subscription',
        },
        dataLayerName: 'SubscriptionDataLayer',
      });

      FacebookService.trackSubscribe(data.price);

      if (data.papName) {
        const decryptedPapName = await decryptAffiliate(data.papName);
        if (decryptedPapName) data.papName = decryptedPapName;
      }

      // @ts-ignore
      window.PapRegisterLead(
        formData.sessionId,
        data.papData1,
        data.papData2,
        data.papData3,
        data.papName
      );

      Dcb.setPromoStep(1);

      try {
        if (localStorage) {
          localStorage.setItem('isPromoValid', 'false');
          localStorage.setItem('appliedPromocode', '');
        }
      } catch (err) {}

      const search = buildFlowSearch();

      if (formData.source === 'api-app') {
        try {
          await axios.get(
            `https://s2s.adjust.com/event?s2s=1&event_token=6dqpmp&app_token=28h6opytxvb4&&gps_adid=ebd76ecb-9fe5-4c7e-87d8-2b416a2dd0b6`
          );
        } catch (errr) {}
      }

      if (redirectRoute) {
        history.push({ pathname: redirectRoute, search });
      } else {
        history.push({
          pathname: `/thankyou/${formData.lang}/${formData.sessionId}`,
          state: { activationURL: data.activationURL, isWhite: false },
          search,
        });
      }
    } else {
      await showSubscribeError(message, data?.apiUnifiedResponseCode, formData);
      if (formData.source === 'api-app') {
        window.location.href = `${WEB_PAGE_URL}?status=failure&message=${message}&price=`;
      }
    }
  } catch (err) {
    console.log('submitOTPResponseError', err);
    const moroccoDv = isMoroccoDvContext(formData);
    await setAlert(
      getLocalizedError(PIN_ERROR_BY_LANG, otpRequestBody.lang),
      'danger',
      moroccoDv ? null : undefined,
      moroccoDv ? moroccoDvAlertOptions : undefined
    );
    Dcb.writeOtpFailure(null);

    if (formData.source === 'api-app') {
      window.location.href = `${WEB_PAGE_URL}?status=failure&message=Error&price=`;
    }
  }
};

export const resendOTP = async (formData, history, isV3Route = false) => {
  LogService.dcbClickButtonResend(formData.otp);
  const config = Helpers.getAxiosHeadersConfig();

  const resendOtpRequestBody = {
    sessionId: formData.sessionId,
    lang: formData.lang,
    otp: formData?.otp || '',
    antiFraudUniqueId: formData?.antiFraudUniqueId || '',
    clickId: formData?.clickId || '',
  };

  Dcb.writeResendOtpTick();

  try {
    const res = await axios.post(
      `${API_URL}/home/subscribe/resendotp`,
      resendOtpRequestBody,
      config
    );

    const { data, statusCode, message } = res.data;

    if (statusCode !== 200) {
      await showSubscribeError(message, undefined, formData);
      Dcb.writeResendOtpFailure(null);
      return;
    }

    Dcb.writeResendOtpSuccess(data);
    if (!isV3Route && !isAg26MarketingVariation()) {
      await setAlert('PIN sent!', 'info');
    }
  } catch (err) {
    console.log('resendtOTPResponseError', err);
    await showSupportError(formData);
    Dcb.writeResendOtpFailure(null);
  }
};

export const getThankYou = async (
  sessionId: string | null | undefined,
  isShownMsisdn = false,
  aggregator: string | null = null
) => {
  const config = Helpers.getAxiosHeadersConfig();
  Dcb.writeThankyouRequested();

  try {
    const thankYouUrl = sessionId
      ? `${API_URL}/home/thankyou/${sessionId}`
      : `${API_URL}/home/thankyou`;
    const requestConfig =
      aggregator && !sessionId ? { ...config, params: { aggregator } } : config;

    const res = await axios.get(thankYouUrl, requestConfig);
    const { data, statusCode, message } = res.data;

    if (statusCode === 200) {
      if (sessionId) {
        if (!dcbInitialSnapshot.otpData.subscriberId && !isShownMsisdn) {
          LogService.dcbFinishHostedFlow();
        }

        TagManager.dataLayer({
          dataLayer: {
            event: 'subscribe',
            eventCategory: 'DCB',
            eventAction: 'subscribe',
            eventValue: data.sessionPrice || 'AED',
            eventLabel: 'Plus Subscription',
          },
          dataLayerName: 'SubscriptionDataLayer',
        });

        FacebookService.trackSubscribe(data.sessionPrice);

        if (data.papName) {
          const decryptedPapName = await decryptAffiliate(data.papName);
          if (decryptedPapName) data.papName = decryptedPapName;
        }

        // @ts-ignore
        window.PapRegisterLead(
          data.sessionId,
          data.papData1,
          data.papData2,
          data.papData3,
          data.papName
        );
      }

      Dcb.writeThankyouSuccess(data);

      if (sessionId && data.source === 'api-app') {
        const appendQueryParams = `status=success&message=${message}&price=${data.price}`;

        try {
          await axios.get(
            `https://s2s.adjust.com/event?s2s=1&event_token=6dqpmp&app_token=28h6opytxvb4&&gps_adid=ebd76ecb-9fe5-4c7e-87d8-2b416a2dd0b6`
          );
        } catch (errr) {}

        window.location.href = `${WEB_PAGE_URL}/thankyou?${appendQueryParams}`;
      }
    } else {
      if (sessionId && !dcbInitialSnapshot.otpError && !isShownMsisdn) {
        LogService.dcbFailedHostedFlow();
      }

      await setAlert('Error processing request. Please contact support', 'danger');
      Dcb.writeThankyouFailure(null);

      if (sessionId && data.source === 'api-app') {
        window.location.href = `${WEB_PAGE_URL}?status=failure&message=${message}&price=`;
      }
    }
  } catch (err) {
    await setAlert('Error processing request. Please contact support', 'danger');
    Dcb.writeThankyouFailure(null);
  }
};

export const reachedThankYouPage = () => Dcb.writeReachedThankYouFromOtp();


export const applyPromocode = async (promocode) => {
  console.log('promocode entered ' + promocode);
  Dcb.setPromoStep(2);
  const body = { offerCode: promocode };
  const config = Helpers.getAxiosHeadersConfig();

  const writePromocodeToLS = (isValid: boolean, code = '') => {
    try {
      if (localStorage) {
        localStorage.setItem('isPromoValid', String(isValid));
        localStorage.setItem('appliedPromocode', code);
      }
    } catch (err) {}
  };

  try {
    const res = await axios.post(`${API_URL}/home/offer/validate`, body, config);
    const { data } = res.data;

    if (data && data.points) {
      Dcb.setPromoStep(3);
      Dcb.setOfferCode(promocode);
      writePromocodeToLS(true, promocode);
    } else {
      Dcb.setPromoStep(1);
      Dcb.flagInvalidPromocode();
      writePromocodeToLS(false);
    }
  } catch (err) {
    console.log('error in validateoffer endpoint');
    Dcb.setPromoStep(1);
    Dcb.flagInvalidPromocode();
    writePromocodeToLS(false);
  }
};

export const setPromocodeAsAppied = () => {
  let promocodeLS;
  try {
    promocodeLS = localStorage.getItem('appliedPromocode');
  } catch (err) {}

  Dcb.setPromoStep(3);
  Dcb.setOfferCode(promocodeLS || '');
};

